import React from 'react';
import { Alert, Box, Button, Checkbox, SpaceBetween } from '@amzn/awsui-components-core';
import {
  DuplicationCheckStatus,
  ExternalOperationName,
  OperationCount,
} from '@amzn/awscat-pattern-library-service-model';
import {
  DuplicateScanResultsView,
  DuplicateScanResultItem,
} from '../../../duplicate-scan-results/DuplicateScanResultsView';
import { ThumbsFeedback, ThumbsValue } from '../../../v2-common/rum-feedback';
import { AUTOMATED_PREAUTHORING_EVENT_ID } from '../../../../monitoring/PreapprovalTelemetry';

interface DuplicateCheckResultsProps {
  readonly results: DuplicateScanResultItem[];
  readonly status: DuplicationCheckStatus;
  readonly isContentHarmful: boolean;
  readonly isContentInvalid: boolean;
  readonly isUniqueContentConfirmed: boolean;
  readonly onUniqueContentConfirmedChange: (confirmed: boolean) => void;
  readonly onRequestContributor?: (itemId: string) => void;
  readonly successRateTrackers?: Partial<Record<ExternalOperationName, OperationCount>>;
  readonly onRescan?: () => void;
  /** Called when the user clicks through to a flagged duplicate article. */
  readonly onResultClick?: (articleId: string) => void;
  /** Compose RUM event payload for scan feedback. When provided, a feedback control is shown. */
  readonly onScanFeedback?: (value: ThumbsValue, feedback?: string) => Record<string, unknown>;
}

const UniqueContentCheckbox: React.FC<{
  checked: boolean;
  onChange: (confirmed: boolean) => void;
}> = ({ checked, onChange }) => (
  <Checkbox checked={checked} onChange={({ detail }) => onChange(detail.checked)}>
    <Box variant="span">I confirm my content is unique</Box>
    <Box variant="p" color="text-body-secondary" fontSize="body-s">
      This content will provide unique guidance not covered by existing AWS resources.
    </Box>
  </Checkbox>
);

const DEFAULT_EXPECTED_KEYWORD_CHECKS = 10;

/**
 * Shown when the duplication scan is turned off (e.g. the genai kill switch).
 */
const SCAN_DISABLED_MESSAGE =
  'The duplication scan is temporarily unavailable, so this proposal was not checked. Please review your content for accuracy and completeness before proceeding.';

const SourcesUnavailableWarning: React.FC<{
  successRateTrackers?: Partial<Record<ExternalOperationName, OperationCount>>;
  onRescan?: () => void;
}> = ({ successRateTrackers, onRescan }) => {
  const searchTrackers = successRateTrackers
    ? [
        successRateTrackers[ExternalOperationName.ApglKnowledgeBaseSearch],
        successRateTrackers[ExternalOperationName.AwsKnowledgeBaseSearch],
      ]
    : [];

  const totalRequests = searchTrackers.reduce((sum, t) => sum + (t?.totalRequests ?? 0), 0);
  const failedRequests = searchTrackers.reduce((sum, t) => sum + (t?.failedRequests ?? 0), 0);

  const effectiveTotal = totalRequests > 0 ? totalRequests : DEFAULT_EXPECTED_KEYWORD_CHECKS;
  const successfulRequests = totalRequests - failedRequests;

  if (totalRequests > 0 && failedRequests === 0) return null;

  return (
    <Alert type="warning" action={onRescan && <Button onClick={onRescan}>Rescan</Button>}>
      {successfulRequests} of {effectiveTotal} keyword checks completed successfully. To improve
      results coverage, you can run the scan again.
    </Alert>
  );
};

const DuplicateCheckResults: React.FC<DuplicateCheckResultsProps> = ({
  results,
  status,
  isContentHarmful,
  isContentInvalid,
  isUniqueContentConfirmed,
  onUniqueContentConfirmedChange,
  onRequestContributor,
  successRateTrackers,
  onRescan,
  onResultClick,
  onScanFeedback,
}) => {
  const showSourcesUnavailableWarning =
    status === DuplicationCheckStatus.PARTIAL_SUCCESS || status === DuplicationCheckStatus.FAILED;
  // Harmful takes priority over invalid
  const contentRejectionReason = isContentHarmful ? 'harmful' : isContentInvalid ? 'invalid' : null;

  if (contentRejectionReason) {
    return (
      <SpaceBetween size="l">
        <Alert type="warning" header="Content flagged">
          {contentRejectionReason === 'harmful'
            ? 'Your content has been flagged as potentially harmful. Please review your content and remove any inappropriate material before proceeding.'
            : 'Your content has been flagged as invalid. Please review your content for accuracy and completeness before proceeding.'}
        </Alert>
        <UniqueContentCheckbox
          checked={isUniqueContentConfirmed}
          onChange={onUniqueContentConfirmedChange}
        />
      </SpaceBetween>
    );
  }

  // The scan was intentionally skipped (e.g. the genai kill switch). Show the
  // server's message (or a fallback) and no Rescan — a rescan cannot succeed
  // while the scan is disabled — but still let the user confirm and proceed.
  if (status === DuplicationCheckStatus.DISABLED) {
    return (
      <SpaceBetween size="l">
        <Alert type="warning">{SCAN_DISABLED_MESSAGE}</Alert>
        <UniqueContentCheckbox
          checked={isUniqueContentConfirmed}
          onChange={onUniqueContentConfirmedChange}
        />
      </SpaceBetween>
    );
  }

  if (status === DuplicationCheckStatus.FAILED) {
    return (
      <SpaceBetween size="l">
        <SourcesUnavailableWarning successRateTrackers={successRateTrackers} onRescan={onRescan} />
        <UniqueContentCheckbox
          checked={isUniqueContentConfirmed}
          onChange={onUniqueContentConfirmedChange}
        />
      </SpaceBetween>
    );
  }

  if (results.length === 0) {
    if (showSourcesUnavailableWarning) {
      return (
        <SpaceBetween size="l">
          <SourcesUnavailableWarning
            successRateTrackers={successRateTrackers}
            onRescan={onRescan}
          />
          <UniqueContentCheckbox
            checked={isUniqueContentConfirmed}
            onChange={onUniqueContentConfirmedChange}
          />
        </SpaceBetween>
      );
    }

    return (
      <Alert type="success" header="No duplicate content found">
        Congratulations! The duplication scan didn't find any matching results. Please choose the{' '}
        <Box variant="span" fontWeight="bold">
          Create draft
        </Box>{' '}
        button to start writing your content.
      </Alert>
    );
  }

  return (
    <SpaceBetween size="l">
      <Box variant="p">
        We've scanned public AWS resources and APG Library for content that is similar to your
        proposal. Review these results to confirm your proposed content doesn't duplicate these
        publications.
      </Box>
      {showSourcesUnavailableWarning && (
        <SourcesUnavailableWarning successRateTrackers={successRateTrackers} onRescan={onRescan} />
      )}
      <DuplicateScanResultsView
        results={results}
        onRequestContributor={onRequestContributor}
        onResultClick={onResultClick}
      />
      <UniqueContentCheckbox
        checked={isUniqueContentConfirmed}
        onChange={onUniqueContentConfirmedChange}
      />
      {onScanFeedback && (
        <ThumbsFeedback
          eventType={AUTOMATED_PREAUTHORING_EVENT_ID}
          label="Are these results helpful?"
          composeEvent={onScanFeedback}
        />
      )}
    </SpaceBetween>
  );
};

export default DuplicateCheckResults;
